import { visibleSections } from '../sections/types';
import type { Section } from '../sections/types';
import type { Task } from '../tasks/types';

/**
 * Sections as filter tabs above the board.
 *
 * They used to be COLUMNS, under a "Group by: Status | Section" toggle. That
 * was the flaw: choosing Section replaced the status columns entirely, so the
 * board stopped showing what stage anything was at and lost the Completed
 * column with it. A board that can only answer one question at a time answers
 * the wrong one half the time.
 *
 * As tabs, the two questions compose. The columns always mean status; the tab
 * narrows which tasks are being asked about. "All Tasks" is not a section — it
 * is the absence of the filter, which is why it carries no id.
 *
 * The tab writes filters.sectionId, the same field the toolbar's Section
 * dropdown writes. One piece of state, so the two controls cannot disagree,
 * and the choice rides along into a saved view without any new plumbing.
 */

export const ALL_TASKS_TAB_KEY = 'all';
export const NO_SECTION_TAB_KEY = 'none';

export interface SectionTab {
  /** 'all', 'none', or the section id. Distinguishes the two null-ish cases. */
  key: string;
  name: string;
  count: number;
  /**
   * What filters.sectionId becomes. undefined clears the filter; null is the
   * real choice "tasks in no section at all", which the filter treats as a
   * value like any other.
   */
  sectionId: string | null | undefined;
}

/**
 * The tab strip. Counts are of top-level tasks, matching what the board draws
 * as cards — counting subtasks would make the numbers disagree with the
 * columns underneath them.
 *
 * "No section" appears only when something is actually in it. An empty tab
 * that can never be anything but empty is a control that does nothing.
 */
export function sectionTabs(sections: Section[], tasks: Task[]): SectionTab[] {
  const cards = tasks.filter((task) => task.parent_task_id === null);
  const open = visibleSections(sections);
  const known = new Set(open.map((section) => section.id));

  const tabs: SectionTab[] = [
    {
      key: ALL_TASKS_TAB_KEY,
      name: 'All Tasks',
      count: cards.length,
      sectionId: undefined,
    },
    ...open.map((section) => ({
      key: section.id,
      name: section.name,
      count: cards.filter((task) => task.section_id === section.id).length,
      sectionId: section.id,
    })),
  ];

  // Archived sections are not tabs — they are hidden by design — but their
  // tasks are not lost, and this is where they surface. Same rule the List
  // View follows.
  const loose = cards.filter((task) => task.section_id === null || !known.has(task.section_id));
  if (loose.length > 0) {
    tabs.push({
      key: NO_SECTION_TAB_KEY,
      name: 'No section',
      count: loose.length,
      sectionId: null,
    });
  }

  return tabs;
}

/** Which tab is lit, given the filter. Nothing set means All Tasks. */
export function activeTabKey(sectionId: string | null | undefined): string {
  if (sectionId === undefined) {
    return ALL_TASKS_TAB_KEY;
  }
  return sectionId === null ? NO_SECTION_TAB_KEY : sectionId;
}
